import SkeletonBox from '@src/components/common/SkeletonBox';
import { cssAspectRatio } from '@src/components/content-components/hooks/useRenderedAspectRatio';
import styled from 'styled-components';

type Props = {
  /** The wait's one label: the verb in flight, in the subtitle slot. */
  message: string;
  /** The clip canvas shape, so the placeholder cards are the height of the real ones. */
  aspectRatio?: string;
  onCancel?: () => void;
};

const PLACEHOLDER_CARDS = 6;

/**
 * The results modal (`AIClipProducerResults`) before it has results: the same
 * shell, title, summary row and 3-column grid, drawn in `SkeletonBox` — so the
 * wait already looks like what is coming, and the modal does not reset from
 * form to spinner to results. The one live element is the subtitle, which
 * carries the "Generating clips…" copy (and a Cancel while retrying) where the
 * "N stories generated" count will land.
 */
export default function AIClipProducerResultsSkeleton({
  message,
  aspectRatio,
  onCancel,
}: Props) {
  return (
    <Wrapper
      data-testid="ai-clip-producer-results-skeleton"
      role="status"
      aria-live="polite"
      aria-label={message}
    >
      <TitleLine $width="320px" $height="20px" />
      <Subtitle>
        <span>{message}</span>
        {onCancel && (
          <CancelButton type="button" onClick={onCancel}>
            Cancel
          </CancelButton>
        )}
      </Subtitle>
      <SummaryRow>
        <SkeletonBox $width="120px" $height="12px" />
        <SkeletonBox $width="90px" $height="12px" />
        <SkeletonBox $width="80px" $height="12px" />
        <SkeletonBox $width="130px" $height="12px" />
      </SummaryRow>
      <TopBar>
        <Row $gap="16px">
          <SkeletonBox $width="110px" $height="16px" />
          <SkeletonBox $width="60px" $height="16px" />
        </Row>
        <Row $gap="8px">
          <SkeletonBox
            $width="150px"
            $height="var(--btn-h-lg)"
            $radius="var(--radius-button)"
          />
          <SkeletonBox
            $width="160px"
            $height="var(--btn-h-lg)"
            $radius="var(--radius-button)"
          />
        </Row>
      </TopBar>
      <Clips>
        {Array.from({ length: PLACEHOLDER_CARDS }, (_, i) => (
          <Card key={i}>
            <ScoreRow>
              <SkeletonBox $width="44px" $height="28px" />
              <SkeletonBox $width="18px" $height="18px" $radius="50%" />
            </ScoreRow>
            <Canvas $aspectRatio={cssAspectRatio(aspectRatio) ?? '16 / 9'} />
            <SkeletonBox $width="85%" $height="14px" />
            <Snippet>
              <SkeletonBox $height="11px" />
              <SkeletonBox $width="70%" $height="11px" />
            </Snippet>
            <MetaRow>
              <SkeletonBox $width="64px" $height="10px" />
              <SkeletonBox $width="88px" $height="22px" $radius="999px" />
            </MetaRow>
          </Card>
        ))}
      </Clips>
    </Wrapper>
  );
}

/* Mirrors `AIClipProducerResults`' Wrapper 1:1, so the results land in the
   same box. */
const Wrapper = styled.div`
  border-radius: 10px;
  background-color: var(--background);
  width: 942px;
  max-width: calc(100vw - 80px);
  border: 1px solid var(--border);
  height: calc(100vh - 140px);
  max-height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 40px 24px 24px;
  color: var(--foreground);
`;

/* The real title is a 20px line, centred. */
const TitleLine = styled(SkeletonBox)`
  margin: 0 auto 6px;
`;

/* The results' Heading, and the wait's one label. */
const Subtitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  min-height: 21px;
  color: var(--text-secondary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  line-height: 150%;
`;

/* Ghost, sm — the same escape hatch OrbLoadingState offers. */
const CancelButton = styled.button`
  height: var(--btn-h-sm);
  padding: 0 var(--btn-px-sm);
  border: none;
  border-radius: var(--radius-button);
  background: none;
  color: var(--text-muted);
  font-family: inherit;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;

  &:hover {
    background: var(--border);
    color: var(--text-secondary);
  }
`;

/* SelectionSummaryBar: one line of label/value pairs, 6px vertical padding. */
const SummaryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 6px 0 6px 8px;
`;

const TopBar = styled.div`
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Row = styled.div<{ $gap: string }>`
  display: flex;
  align-items: center;
  gap: ${props => props.$gap};
`;

/* The results' ScrollArea + Clips grid, minus the scrollbar gutter: the
   skeleton never overflows, and the 2px side padding keeps the columns where
   the real grid puts them. */
const Clips = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-top: 24px;
  padding: 0 2px 8px;
  overflow: hidden;
  min-height: 0;
`;

/* AIClip's Card: 8/16/16 padding, borderless surface, 8px radius. */
const Card = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 16px 16px;
  border-radius: 8px;
  background-color: var(--surface);
  box-sizing: border-box;
  min-width: 0;
`;

const ScoreRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 34px;
  margin-bottom: 8px;
`;

/* Bleeds the card's gutters like AIClip's VideoBleed; square corners, as the
   real canvas has. */
const Canvas = styled(SkeletonBox)<{ $aspectRatio: string }>`
  height: auto;
  width: auto;
  margin: 0 -16px 12px;
  border-radius: 0;
  aspect-ratio: ${props => props.$aspectRatio};
`;

const Snippet = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 8px 0 16px;
`;

const MetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
`;
